export interface MedicationCatalogSortEntry {
  product_name?: string | null;
  concentration?: string | null;
  package_quantity?: string | null;
  pharmaceutical_form?: string | null;
  presentation_raw?: string | null;
  has_verified_posology?: boolean;
  score?: number;
}

/** Embalagens acima disso descem no fim do grupo verificado (lista verde). */
export const BULK_COMPRIMIDO_THRESHOLD = 80;

/** Extrai quantidade de comprimidos da apresentação, quando identificável. */
export function extractComprimidoCount(entry: Pick<
  MedicationCatalogSortEntry,
  'package_quantity' | 'pharmaceutical_form' | 'presentation_raw'
>): number | null {
  const form = String(entry.pharmaceutical_form || '').toLowerCase();
  const isComprimidoForm = form.includes('comprimido');

  for (const source of [entry.package_quantity, entry.presentation_raw]) {
    if (!source) continue;
    const text = String(source).toLowerCase();
    if (!isComprimidoForm && !/\bcomprimidos?\b/.test(text)) continue;

    const labeled = text.match(/(\d+(?:[.,]\d+)?)\s*comprimidos?\b/i);
    if (labeled) {
      const value = Number.parseFloat(labeled[1].replace(',', '.'));
      if (Number.isFinite(value)) return value;
    }

    if (isComprimidoForm) {
      const leading = text.match(/^(\d+(?:[.,]\d+)?)\b/);
      if (leading) {
        const value = Number.parseFloat(leading[1].replace(',', '.'));
        if (Number.isFinite(value)) return value;
      }
    }
  }

  return null;
}

export function isBulkComprimidoPack(
  entry: Pick<MedicationCatalogSortEntry, 'package_quantity' | 'pharmaceutical_form' | 'presentation_raw'>,
): boolean {
  const count = extractComprimidoCount(entry);
  return count !== null && count > BULK_COMPRIMIDO_THRESHOLD;
}

export function compareMedicationCatalogEntries(
  left: MedicationCatalogSortEntry,
  right: MedicationCatalogSortEntry,
): number {
  const verifiedDiff =
    Number(Boolean(right.has_verified_posology)) - Number(Boolean(left.has_verified_posology));
  if (verifiedDiff !== 0) return verifiedDiff;

  if (left.has_verified_posology && right.has_verified_posology) {
    const bulkDiff =
      Number(isBulkComprimidoPack(left)) - Number(isBulkComprimidoPack(right));
    if (bulkDiff !== 0) return bulkDiff;
  }

  const scoreDiff = Number(right.score || 0) - Number(left.score || 0);
  if (scoreDiff !== 0) return scoreDiff;

  const nameDiff = String(left.product_name || '').localeCompare(
    String(right.product_name || ''),
    'pt-BR',
  );
  if (nameDiff !== 0) return nameDiff;

  return String(left.concentration || '').localeCompare(String(right.concentration || ''), 'pt-BR');
}

export function sortMedicationCatalogResults<T extends MedicationCatalogSortEntry>(
  rows: T[],
): T[] {
  return [...rows].sort(compareMedicationCatalogEntries);
}
